import {
  formatMadeAttempted,
  formatMinutes,
  formatPercentage,
  formatPlusMinus,
  formatQuarterResults,
  formatScoringRun,
  formatSeconds,
  formatSheetDateTime,
  sortRoster,
  teamCode,
} from './sheetFormat'

// Writes every field of the FIBA statistics sheet through `draw(key, text, { align, bold })`,
// reading the numbers from the backend's /sheet response: `stats` (the boxscore) for the player
// lines, `sheet` for plus/minus, intervals, summary and lead tables, `game` for the header.
// Separated from the pdf-lib plumbing (downloadStatisticsPdf) so the cell values are testable
// with a recording drawer. Field keys are those of statisticsCoords.json; team A is home.
//
// Lite games record shots, free throws, fouls, substitutions and the clock, but no rebounds,
// assists, steals, turnovers, blocks or fast-break flag. Every value that needs one of those
// prints "-". `frame(key, rows)` draws the boxes the template does not print (overtime columns).

const NOT_AVAILABLE = '-'
const INTERVAL_MINUTES = [5, 10, 15, 20, 25, 30, 35, 40]
const MAX_OVERTIMES_ON_SHEET = 5

const joinNames = (...people) =>
  people
    .map((p) => (p?.fullName ?? '').trim())
    .filter(Boolean)
    .join(', ')

const orDash = (value) => (value === null || value === undefined ? NOT_AVAILABLE : `${value}`)

// Built from the team name, like the stats app's sheet. The team's own short code is not used:
// it is optional, sometimes shorter than 3 letters and sometimes mixes Latin into Greek.
const code = (team) => teamCode(team?.name)

/* ------------------------------------------------------------- players --- */

// Full box-score row for a player who played. Players who did not play get only number, name
// and "DNP" (see drawTeam), per the sheet rules.
const drawStatLine = (draw, isLite, row, s, plusMinus) => {
  const count = (value) => (isLite ? NOT_AVAILABLE : `${value ?? 0}`)
  const fgm = (s.made2Pointers ?? 0) + (s.made3Pointers ?? 0)
  const fga = (s.attempted2Pointers ?? 0) + (s.attempted3Pointers ?? 0)

  draw(`${row}_min`, formatMinutes(s.minutes))
  draw(`${row}_fg_ma`, formatMadeAttempted(fgm, fga))
  draw(`${row}_fg_pct`, formatPercentage(fgm, fga))
  draw(`${row}_p2_ma`, formatMadeAttempted(s.made2Pointers, s.attempted2Pointers))
  draw(`${row}_p2_pct`, formatPercentage(s.made2Pointers, s.attempted2Pointers))
  draw(`${row}_p3_ma`, formatMadeAttempted(s.made3Pointers, s.attempted3Pointers))
  draw(`${row}_p3_pct`, formatPercentage(s.made3Pointers, s.attempted3Pointers))
  draw(`${row}_ft_ma`, formatMadeAttempted(s.made1Pointers, s.attempted1Pointers))
  draw(`${row}_ft_pct`, formatPercentage(s.made1Pointers, s.attempted1Pointers))
  draw(`${row}_reb_ro`, count(s.offensiveRebounds))
  draw(`${row}_reb_rd`, count(s.defensiveRebounds))
  draw(`${row}_reb_tot`, count((s.offensiveRebounds ?? 0) + (s.defensiveRebounds ?? 0)))
  draw(`${row}_as`, count(s.assists))
  draw(`${row}_to`, count(s.turnovers))
  draw(`${row}_st`, count(s.steals))
  draw(`${row}_bs`, count(s.blocks))
  draw(`${row}_pf_c`, `${s.fouls ?? 0}`)
  draw(`${row}_pf_d`, `${s.foulsDrawn ?? 0}`)
  draw(`${row}_plus_minus`, formatPlusMinus(plusMinus ?? 0))
  draw(`${row}_pts`, `${s.points ?? 0}`, { bold: true })
}

// Team/Coach row (5 boxes) and the bold Totals row (no No./Name/Min/+/-).
const drawTeamRows = (draw, isLite, prefix, teamOnly, totals) => {
  const count = (value) => (isLite ? NOT_AVAILABLE : `${value ?? 0}`)

  const tc = `${prefix}_teamcoach`
  draw(`${tc}_reb_ro`, count(teamOnly.offensiveRebounds))
  draw(`${tc}_reb_rd`, count(teamOnly.defensiveRebounds))
  draw(`${tc}_reb_tot`, count((teamOnly.offensiveRebounds ?? 0) + (teamOnly.defensiveRebounds ?? 0)))
  draw(`${tc}_to`, count(teamOnly.turnovers))
  draw(`${tc}_pf_c`, `${teamOnly.fouls ?? 0}`)

  const t = `${prefix}_totals`
  const bold = { bold: true }
  const fgm = (totals.made2Pointers ?? 0) + (totals.made3Pointers ?? 0)
  const fga = (totals.attempted2Pointers ?? 0) + (totals.attempted3Pointers ?? 0)
  draw(`${t}_fg_ma`, formatMadeAttempted(fgm, fga), bold)
  draw(`${t}_fg_pct`, formatPercentage(fgm, fga), bold)
  draw(`${t}_p2_ma`, formatMadeAttempted(totals.made2Pointers, totals.attempted2Pointers), bold)
  draw(`${t}_p2_pct`, formatPercentage(totals.made2Pointers, totals.attempted2Pointers), bold)
  draw(`${t}_p3_ma`, formatMadeAttempted(totals.made3Pointers, totals.attempted3Pointers), bold)
  draw(`${t}_p3_pct`, formatPercentage(totals.made3Pointers, totals.attempted3Pointers), bold)
  draw(`${t}_ft_ma`, formatMadeAttempted(totals.made1Pointers, totals.attempted1Pointers), bold)
  draw(`${t}_ft_pct`, formatPercentage(totals.made1Pointers, totals.attempted1Pointers), bold)
  draw(`${t}_reb_ro`, count(totals.offensiveRebounds), bold)
  draw(`${t}_reb_rd`, count(totals.defensiveRebounds), bold)
  draw(`${t}_reb_tot`, count((totals.offensiveRebounds ?? 0) + (totals.defensiveRebounds ?? 0)), bold)
  draw(`${t}_as`, count(totals.assists), bold)
  draw(`${t}_to`, count(totals.turnovers), bold)
  draw(`${t}_st`, count(totals.steals), bold)
  draw(`${t}_bs`, count(totals.blocks), bold)
  draw(`${t}_pf_c`, `${totals.fouls ?? 0}`, bold)
  draw(`${t}_pf_d`, `${totals.foulsDrawn ?? 0}`, bold)
  draw(`${t}_pts`, `${totals.points ?? 0}`, bold)
}

const drawTeam = (draw, isLite, prefix, team, headCoach, plusMinus) => {
  draw(`${prefix}_name`, team?.name, { align: 'left', bold: true })
  draw(`${prefix}_head_coach`, headCoach, { align: 'left', bold: true })

  const roster = sortRoster(team?.teamPlayerStats, (p) => p.played === true)
  roster.forEach((p, i) => {
    const row = `${prefix}_player${i + 1}`
    draw(`${row}_no`, `${p.isStarter ? '*' : ''}${p.jersey ?? ''}`, { align: 'right' })
    draw(`${row}_name`, p.fullName, { align: 'left' })
    if (p.played) {
      drawStatLine(draw, isLite, row, p.stats || {}, plusMinus?.[p.gameTeamPlayerId])
    } else {
      draw(`${row}_min`, 'DNP')
    }
  })

  drawTeamRows(draw, isLite, prefix, team?.teamOnlyStats || {}, team?.totalStats || {})
}

/* ----------------------------------------------------------- intervals --- */

const drawIntervalScores = (draw, prefix, team, numbers) => {
  draw(`${prefix}_code_scoring`, code(team), { bold: true })
  INTERVAL_MINUTES.forEach((minute, i) => {
    draw(`${prefix}_score_${minute}min`, orDash(numbers?.intervals?.[i]))
  })
}

const drawOvertimeIntervals = (draw, frame, home, away) => {
  const count = Math.min(
    Math.max(home?.overtimeIntervals?.length || 0, away?.overtimeIntervals?.length || 0),
    MAX_OVERTIMES_ON_SHEET,
  )
  for (let i = 0; i < count; i += 1) {
    const n = i + 1
    frame(`ot${n}_box`, 2) // one row per team, like the Q1-Q4 boxes
    draw(`ot${n}_label`, `OT${n}`, { bold: true })
    draw(`team_a_score_ot${n}`, orDash(home?.overtimeIntervals?.[i]))
    draw(`team_b_score_ot${n}`, orDash(away?.overtimeIntervals?.[i]))
  }
}

/* ------------------------------------------------------------- summary --- */

// Bottom summary tables: team code in each grey header, then the five rows. Null values are
// what the backend sends for a lite game, and print "-".
const drawSummary = (draw, prefix, team, numbers) => {
  const c = code(team)
  draw(`${prefix}_code_summary_left`, c, { bold: true })
  draw(`${prefix}_code_summary_right`, c, { bold: true })
  draw(`${prefix}_points_from_turnovers`, orDash(numbers?.pointsFromTurnovers))
  draw(`${prefix}_points_in_the_paint`, orDash(numbers?.pointsInThePaint))
  draw(`${prefix}_second_chance_points`, orDash(numbers?.secondChancePoints))
  draw(`${prefix}_fast_break_points`, orDash(numbers?.fastBreakPoints))
  draw(`${prefix}_bench_points`, orDash(numbers?.benchPoints))
}

// Right-hand summary table. Lead changes and times tied are merged cells shared by both teams.
const drawLeadStats = (draw, sheet) => {
  const sides = [
    ['team_a', sheet?.home],
    ['team_b', sheet?.away],
  ]
  sides.forEach(([prefix, numbers]) => {
    draw(`${prefix}_biggest_lead`, `${numbers?.biggestLead ?? 0}`)
    draw(`${prefix}_biggest_scoring_run`, formatScoringRun(numbers?.biggestScoringRun))
    draw(`${prefix}_time_leading`, formatSeconds(numbers?.timeLeadingSeconds))
  })
  draw('lead_changes', `${sheet?.leadChanges ?? 0}`)
  draw('times_tied', `${sheet?.timesTied ?? 0}`)
}

/* --------------------------------------------------------------- entry --- */

const fillStatisticsSheet = (draw, response, frame = () => {}) => {
  const game = response?.game || {}
  const stats = response?.stats || {}
  const sheet = response?.sheet || {}
  const [home = {}, away = {}] = stats.teams || []
  const isLite = stats.mode !== 'Full'
  const officials = game.officials || {}

  // Header
  draw('competition_title', game.competitionName, { bold: true })
  draw('game_title', `${home.name || 'Home'} vs ${away.name || 'Away'}`, { bold: true })
  draw('referees', joinNames(officials.crewChief, officials.umpire1, officials.umpire2), { align: 'left' })
  draw('commissioner', officials.commissioner?.fullName, { align: 'left' })
  draw('game_number', game.externalId)
  draw('place', game.courtName || game.place, { align: 'left' })
  draw('date_time', formatSheetDateTime(game.startDateTime), { align: 'right' })

  // Score
  draw('final_score', `${home.score ?? 0} - ${away.score ?? 0}`, { bold: true })
  draw('quarter_results', formatQuarterResults(home.periodScores, away.periodScores, sheet.periodHasStats))

  // Scoring by 5 minute intervals
  drawIntervalScores(draw, 'team_a', home, sheet.home)
  drawIntervalScores(draw, 'team_b', away, sheet.away)
  drawOvertimeIntervals(draw, frame, sheet.home, sheet.away)

  // Teams
  drawTeam(draw, isLite, 'team_a', home, officials.homeCoach?.fullName, sheet.plusMinus)
  drawTeam(draw, isLite, 'team_b', away, officials.awayCoach?.fullName, sheet.plusMinus)

  // Bottom summary tables
  drawSummary(draw, 'team_a', home, sheet.home)
  drawSummary(draw, 'team_b', away, sheet.away)
  drawLeadStats(draw, sheet)
}

export default fillStatisticsSheet
